import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MAX_CHANNEL_LINK } from "@/config/links";
import { LANDING } from "@/constants/testIds";
import { trackClick } from "@/lib/track";
import { fadeUp, revealProps, scaleIn, stagger } from "@/lib/motion";
import BUBLIK_IMAGE_URL from "@/assets/bublik.webp";

const DAYS = [
  { day: "Пн", rubric: "Сказка недели", example: "«Маленький фонарик, который решился светить первым»" },
  { day: "Вт", rubric: "Сказка-помощник", example: "«Садик и утреннее расставание»" },
  { day: "Ср", rubric: "Дети-герои", example: "«Ребёнок запускает воздушного змея»" },
  { day: "Чт", rubric: "Личная сказка", example: "«Ребёнок спасает Сонный город»" },
  { day: "Пт", rubric: "Сказка перед сном", example: "«Подушка, которая собирала добрые сны»" },
  { day: "Сб", rubric: "Сказка для мамы", example: "«Мама имеет право на пять тихих минут»" },
  { day: "Вс", rubric: "Воскресенье с Бубликом", example: "«Пёс Бублик и первая вечерняя сказка»" },
];

// getDay(): 0 — воскресенье, а лента начинается с понедельника.
const todayIndex = (new Date().getDay() + 6) % 7;

const Channel = () => {
  const [emblaRef, embla] = useEmblaCarousel({ align: "start", containScroll: "trimSnaps" });
  const [selected, setSelected] = useState(todayIndex);
  const [snaps, setSnaps] = useState([]);

  const onSelect = useCallback(() => {
    if (embla) setSelected(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return undefined;
    setSnaps(embla.scrollSnapList());
    embla.scrollTo(todayIndex, true);
    onSelect();
    embla.on("select", onSelect);
    embla.on("reInit", onSelect);
    return () => {
      embla.off("select", onSelect);
      embla.off("reInit", onSelect);
    };
  }, [embla, onSelect]);

  return (
    <section id="channel" className="relative overflow-hidden bg-night-900 py-16 md:py-24">
      <div className="glow-warm pointer-events-none absolute inset-0 opacity-50" />

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
        <motion.div {...revealProps} variants={stagger(0.08)} className="mb-10 text-center">
          <motion.p variants={fadeUp} className="mb-3 text-[11px] uppercase tracking-[0.22em] text-amber">
            MAX-канал
          </motion.p>
          <motion.h2 variants={fadeUp} className="mb-4 font-serif text-3xl text-cream sm:text-4xl lg:text-5xl">
            Каждый вечер новая сказка
          </motion.h2>
          <motion.p variants={fadeUp} className="mx-auto max-w-xl leading-relaxed text-muted-foreground">
            Бесплатно, в 18:00, с озвучкой. У каждого дня своё настроение
          </motion.p>
        </motion.div>

        <motion.div {...revealProps} variants={fadeUp}>
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="-ml-4 flex touch-pan-y">
              {DAYS.map(({ day, rubric, example }, i) => {
                const isToday = i === todayIndex;
                return (
                  <div
                    key={day}
                    className="min-w-0 shrink-0 grow-0 basis-[78%] pl-4 sm:basis-[46%] lg:basis-[30%]"
                  >
                    <article
                      data-testid={`${LANDING.weeklyRhythmPrefix}${day}`}
                      className={`flex h-full flex-col gap-2 rounded-3xl border p-6 backdrop-blur-xl transition-colors duration-300 ${
                        isToday
                          ? "border-amber/45 bg-gradient-to-br from-amber/[0.16] via-white/[0.05] to-transparent shadow-glow"
                          : "border-line bg-white/[0.04]"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-xs font-semibold uppercase tracking-[0.18em] ${
                            isToday ? "text-amber" : "text-dim"
                          }`}
                        >
                          {day}
                        </span>
                        {isToday ? (
                          <span className="rounded-full bg-amber px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-night-900">
                            сегодня
                          </span>
                        ) : null}
                      </div>
                      <h3 className="font-serif text-xl leading-snug text-cream">{rubric}</h3>
                      <p className="text-sm leading-relaxed text-muted-foreground">{example}</p>
                    </article>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-7 flex items-center justify-center gap-2">
            {snaps.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`День ${DAYS[i].day}`}
                onClick={() => embla?.scrollTo(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === selected ? "w-7 bg-amber" : "w-2 bg-line-strong hover:bg-muted-foreground"
                }`}
              />
            ))}
          </div>
        </motion.div>

        <motion.div
          {...revealProps}
          variants={stagger(0.1)}
          className="mt-16 grid items-center gap-10 md:mt-20 md:grid-cols-2"
        >
          <motion.div variants={scaleIn} className="flex justify-center md:order-2">
            <div className="relative w-52 max-w-xs sm:w-64 md:w-full">
              <div className="absolute inset-4 rounded-full bg-amber/25 blur-3xl" />
              {/* Стикерный вид: покачивается сам, на hover — наклон и приближение */}
              <motion.img
                data-testid={LANDING.bublikImage}
                src={BUBLIK_IMAGE_URL}
                alt="Пёс Бублик — герой воскресных сказок канала"
                whileHover={{ rotate: -5, scale: 1.05 }}
                transition={{ type: "spring", stiffness: 260, damping: 18 }}
                className="relative w-full animate-sway drop-shadow-[0_18px_35px_rgba(10,12,34,0.65)]"
              />
            </div>
          </motion.div>

          <div className="text-center md:order-1 md:text-left">
            <motion.h3 variants={fadeUp} className="mb-5 font-serif text-2xl text-cream sm:text-3xl lg:text-4xl">
              Бублик приходит по воскресеньям
            </motion.h3>
            <motion.p variants={fadeUp} className="mb-4 leading-relaxed text-muted-foreground md:text-lg">
              Тёплый домашний пёсик, который каждое воскресенье приходит с новой историей — про
              щётку с улыбкой, красный мячик дружбы и тихий двор. Простой и узнаваемый герой,
              которого легко полюбить и ждать снова.
            </motion.p>
            <motion.p variants={fadeUp} className="text-sm text-dim">
              Подпишитесь на канал — и Бублик будет заходить каждое воскресенье.
            </motion.p>
          </div>
        </motion.div>

        <motion.div {...revealProps} variants={fadeUp} className="mt-12 flex justify-center">
          <a
            data-testid={LANDING.bublikCta}
            href={MAX_CHANNEL_LINK}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackClick("channel")}
            className="inline-flex min-h-[3.25rem] items-center justify-center gap-2 rounded-full border border-line-strong bg-white/[0.03] px-7 font-semibold text-cream transition-colors duration-300 hover:border-amber/60 hover:text-amber-soft"
          >
            Подписаться на канал
            <ArrowRight className="h-4 w-4 shrink-0" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Channel;
